import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/Spinner';
import { useAuthStore } from '@/stores/authStore';
import { useAcceptConsent, useConsentStatus } from './useConsent';

/**
 * Blocks the entire authenticated UI behind a personal-data-processing
 * consent screen. The acceptance is fetched from the API on every session
 * and recorded server-side with a tgId / acceptedAt / version audit trail.
 *
 * Design choice: only enforce the gate when the user is fully authenticated.
 * For dev/preview without Telegram auth (status: unauthenticated/error in
 * ProtectedRoute) we show the children directly so the UI can be inspected.
 *
 * `/legal/*` routes pass through unconditionally so the user can read the
 * policy and consent text *before* deciding whether to accept.
 */
export function ConsentGate({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();
  const authed = status === 'authenticated';
  // While reading legal docs, do not block — otherwise the user can't see
  // what they're being asked to accept.
  const isReadingLegal = location.pathname.startsWith('/legal/');
  const { data, isLoading, isError } = useConsentStatus(authed && !isReadingLegal);
  const accept = useAcceptConsent();

  if (!authed) return <>{children}</>;
  if (isReadingLegal) return <>{children}</>;
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-hint">
        <Spinner />
      </div>
    );
  }
  if (data?.accepted) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center bg-[var(--ink-0)]">
      {/* Atmospheric backdrop — same ember ribbon vocabulary as the main app. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 120% 55% at 72% -20%, rgba(194,58,46,0.18), transparent 65%), radial-gradient(ellipse 80% 40% at 10% 110%, rgba(255,255,255,0.025), transparent 70%)',
        }}
      />

      <div
        className="relative w-full max-w-md p-6 pt-8 pb-[max(env(safe-area-inset-bottom),1.5rem)] flex flex-col gap-5"
        style={{
          background: 'rgba(20,20,22,0.92)',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid var(--line-2)',
        }}
      >
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
            Перед началом
            {data?.version ? ` · версия ${data.version}` : null}
          </span>
          <h1 className="headline text-[20px] uppercase tracking-[0.02em]">
            Согласие на обработку персональных данных
          </h1>
          <span className="hairline w-12" />
        </div>

        <p className="text-[14px] leading-[1.5] text-white/80">
          Нажимая кнопку, вы соглашаетесь на обработку персональных данных в
          соответствии с{' '}
          <Link
            to="/legal/privacy_policy"
            className="underline decoration-white/50 hover:decoration-white"
            style={{ color: '#6ea987' }}
          >
            Политикой обработки ПДн
          </Link>{' '}
          и даёте{' '}
          <Link
            to="/legal/consent"
            className="underline decoration-white/50 hover:decoration-white"
            style={{ color: '#6ea987' }}
          >
            Согласие на обработку персональных данных
          </Link>
          .
        </p>

        {isError ? (
          <p className="text-xs" style={{ color: 'var(--ember)' }}>
            Не удалось загрузить статус согласия. Проверьте подключение и
            попробуйте позже.
          </p>
        ) : null}
        {accept.isError ? (
          <p className="text-xs" style={{ color: 'var(--ember)' }}>
            Не удалось зафиксировать согласие. Попробуйте ещё раз.
          </p>
        ) : null}

        <button
          type="button"
          disabled={accept.isPending || !data?.version}
          onClick={() => accept.mutate(data?.version ?? '')}
          className="rounded-full py-3.5 font-display tracking-[0.08em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-transform active:scale-[0.98]"
          style={{
            background: '#ffffff',
            color: '#0a0a0b',
            boxShadow:
              '0 10px 28px -12px rgba(255,255,255,0.35), 0 0 30px -12px rgba(110,169,135,0.5)',
          }}
        >
          {accept.isPending ? <Spinner className="w-4 h-4" /> : null}
          <span className="text-[13px]">Соглашаюсь</span>
        </button>
      </div>
    </div>
  );
}
